import { numberLocale } from '@/utils/locale';
import { getObjectColors } from '@/utils/objectColors';
import type { AssistantEmployee, AssistantTask, DueCategory, SuggestionFit, SuggestionsDto } from './assistantTypes';

/** Minutes since midnight → "HH:mm". */
export function fmtTime(minutes: number | null | undefined): string {
  const m = Math.max(0, Math.round(minutes ?? 0));
  const h = Math.floor(m / 60) % 24;
  return `${String(h).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** "13:00–14:00" (en dash, no spaces). */
export function fmtRange(from: number | null | undefined, to: number | null | undefined): string {
  return `${fmtTime(from)}\u2013${fmtTime(to)}`;
}

/** Minutes → "3,75h" / "1h" in the UI language. */
export function fmtHours(minutes: number | null | undefined, lang?: string): string {
  const hours = Math.max(0, minutes ?? 0) / 60;
  const value = new Intl.NumberFormat(numberLocale(lang), { maximumFractionDigits: 2 }).format(
    Math.round(hours * 100) / 100,
  );
  return `${value}h`;
}

/** Same as fmtHours without the unit, for the big KPI value. */
export function fmtHoursValue(minutes: number | null | undefined, lang?: string): string {
  return fmtHours(minutes, lang).replace(/h$/, '');
}

/** Kilometres → "400 m" / "2,1 km". */
export function fmtDistance(km: number, lang?: string): string {
  if (km < 1) {
    const meters = Math.max(50, Math.round((km * 1000) / 50) * 50);
    return `${meters}\u00A0m`;
  }
  const value = new Intl.NumberFormat(numberLocale(lang), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(km);
  return `${value}\u00A0km`;
}

export function firstName(name: string | null | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function asDueCategory(value: string | null | undefined): DueCategory {
  return value === 'Day' || value === 'Week' || value === 'Month' ? value : 'None';
}

/** Same key as the week view (TaskBlock/TimelineRow) so object colours match. */
export function objectColorKey(objectId: string | null | undefined, objectName?: string | null): string | null {
  return objectId ?? objectName ?? null;
}

/** An employee can take tasks when the day has a working window and no absence. */
export function isAvailable(e: AssistantEmployee): boolean {
  return !e.absenceTypeName && e.workStart != null && e.workEnd != null;
}

/**
 * Fits that place the task, best first (the backend already sorts by score). The backend lists
 * only fitting pairs; why an employee is missing comes from the employee (see notFitText).
 */
export function okFits(data: SuggestionsDto | null): SuggestionFit[] {
  return (data?.fits ?? []).filter((f) => f.start != null && f.end != null);
}

export function fitsForEmployee(data: SuggestionsDto | null, employeeId: string): SuggestionFit[] {
  return okFits(data).filter((f) => f.employeeId === employeeId);
}

export function fitsForTask(data: SuggestionsDto | null, taskId: string): SuggestionFit[] {
  return okFits(data).filter((f) => f.taskId === taskId);
}

/** Employees without a fit for the task — the reason comes from their availability (notFitText). */
export function unplacedEmployeesForTask(data: SuggestionsDto | null, taskId: string): AssistantEmployee[] {
  const placed = new Set(fitsForTask(data, taskId).map((f) => f.employeeId));
  return (data?.employees ?? []).filter((e) => !placed.has(e.employeeId));
}

export function taskById(data: SuggestionsDto | null, taskId: string | null | undefined): AssistantTask | undefined {
  if (!taskId) return undefined;
  return data?.tasks?.find((t) => t.taskId === taskId);
}

export function employeeById(
  data: SuggestionsDto | null,
  employeeId: string | null | undefined,
): AssistantEmployee | undefined {
  if (!employeeId) return undefined;
  return data?.employees?.find((e) => e.employeeId === employeeId);
}

/** Longest free gap in minutes — the server value, else computed from the gaps. */
export function longestGap(e: AssistantEmployee): number {
  if (e.longestGapMinutes != null) return e.longestGapMinutes;
  return Math.max(0, ...(e.gaps ?? []).map((g) => (g.to ?? 0) - (g.from ?? 0)));
}

/** Day-line preview of planning `task` via `fit`. */
export function ghostOf(fit: SuggestionFit | null | undefined, task: AssistantTask | null | undefined) {
  if (!fit || !task || fit.start == null || fit.end == null) return null;
  return {
    start: fit.start,
    end: fit.end,
    travelIn: fit.travelInMinutes ?? 0,
    objectId: task.objectId,
    objectName: task.objectName,
    objectShortName: task.objectShortName,
  };
}

/** Objects by id, for reason texts and map positions. */
export function objectMap(data: SuggestionsDto | null) {
  return new Map((data?.objects ?? []).filter((o) => o.objectId).map((o) => [o.objectId as string, o]));
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Backend availability codes (AssistantEmployeeDto.availability). */
export type Availability = 'available' | 'absence' | 'noWorkday' | 'dayFull';

export function availabilityOf(e: AssistantEmployee): Availability {
  const a = e.availability;
  if (a === 'available' || a === 'absence' || a === 'noWorkday' || a === 'dayFull') return a;
  // Payload without the field: derive it like the backend.
  if (e.absenceTypeName) return 'absence';
  if (e.workStart == null || e.workEnd == null) return 'noWorkday';
  return (e.gaps ?? []).length > 0 ? 'available' : 'dayFull';
}

/** Localised reason why the employee takes no task today: absence type, no working day or day full. */
export function unavailableText(t: Translate, e: AssistantEmployee): string {
  switch (availabilityOf(e)) {
    case 'absence':
      return e.absenceTypeName || t('assistant.notFit.absence');
    case 'noWorkday':
      return t('assistant.notFit.noWorkday');
    default:
      return t('assistant.notFit.dayFull');
  }
}

/**
 * Localised reason why `employee` has no fit for a task. The backend lists only fitting pairs, so
 * an available employee without a fit lacks a long enough gap ("gapTooSmall").
 */
export function notFitText(t: Translate, employee: AssistantEmployee, lang?: string): string {
  if (availabilityOf(employee) === 'available') {
    return t('assistant.notFit.gapTooSmall', { hours: fmtHours(longestGap(employee), lang) });
  }
  return unavailableText(t, employee);
}

const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;

/**
 * Route/marker colour of an employee: the avatar colour when it is a valid hex value (it goes into
 * Leaflet divIcon HTML, so nothing else may pass), else a stable colour derived from the employee id.
 */
export function employeeTone(e: Pick<AssistantEmployee, 'employeeId' | 'name' | 'avatarColor'>): string {
  const color = e.avatarColor?.trim();
  return color && HEX_COLOR.test(color) ? color : getObjectColors(e.employeeId ?? e.name ?? null).color;
}
